import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { SmartImage } from "@/components/SmartImage";
import { CtaBand } from "@/components/Section";
import { IMAGES } from "@/data/site";

// Approved content — verbatim.
const STEPS = [
  {
    no: "01",
    title: "Customized Cleaning Plan",
    body: "We begin by discussing the facility, your specific requirements and the details that matter most. The service register will define what areas are appropriate and corresponding procedures are set in place. With our full complement of services and partners , we aim to provide a full integrated service platform in which you need to only make one phone call.",
    image: IMAGES.handshake,
    alt: "Business handshake confirming a customized cleaning plan",
  },
  {
    no: "02",
    title: "Written Schedule & Responsibilities",
    body: "The legacy process used is a detailed day-by-day schedule frequency chart that outlines responsibilities.",
    image: IMAGES.corridor,
    alt: "Janitorial cart and documented schedule in a commercial corridor",
  },
  {
    no: "03",
    title: "Consistent Site Team",
    body: "At our core are trained associates specifically assigned to your site, with a written list and schedule so everyone operates toward the same expectations. Moreover, their sole responsibility during a given month is to ensure that your site meets what you expect.",
    image: IMAGES.officeMop,
    alt: "Consistent trained team maintaining a commercial office",
  },
  {
    no: "04",
    title: "Weekly Follow-Up",
    body: "Since 1989, we haven’t changed our core belief, which is that we “inspect what we expect” and each site is visited in person weekly or monthly depending on the complexity of your location.",
    image: IMAGES.officeWetFloor,
    alt: "In-person site inspection during commercial floor scrubbing",
  },
  {
    no: "05",
    title: "Rapid Problem Resolution",
    body: "When a customer needs a change or identifies something that was not completed to expectation, the brochure promises immediate “On-The-Spot Service” and a quick return to normal operations.",
    image: IMAGES.carpetCare,
    alt: "Rapid on-the-spot cleaning response in a commercial space",
  },
  {
    no: "06",
    title: "Management Oversight",
    body: "Site managers are available daily as they are constantly in the field and easily reachable by phone, email or text.",
    image: IMAGES.moodMop,
    alt: "Site manager overseeing detailed cleaning in the field",
  },
];

const Step = ({ step, index }) => {
  const flip = index % 2 === 1;
  return (
    <div data-testid={`process-step-${step.no}`} className="relative lg:grid lg:grid-cols-2 lg:gap-x-24 lg:items-center">
      {/* Center node (desktop) */}
      <div className="absolute left-1/2 top-2 z-10 hidden -translate-x-1/2 lg:block">
        <span className="flex h-14 w-14 items-center justify-center rounded-full border-4 border-white bg-navy font-display text-base font-black text-white shadow-lg">
          {step.no}
        </span>
      </div>

      {/* Image */}
      <Reveal className={flip ? "lg:order-2 lg:pl-16" : "lg:order-1 lg:pr-16"}>
        <SmartImage src={step.image} alt={step.alt} ratio="aspect-[4/3]" imgClassName="hover:scale-105" />
      </Reveal>

      {/* Text */}
      <Reveal delay={0.1} className={flip ? "lg:order-1 lg:pr-16 lg:text-right" : "lg:order-2 lg:pl-16"}>
        <div className="mt-6 lg:mt-0">
          <span className={`inline-flex h-10 w-10 items-center justify-center bg-brand-green font-display text-sm font-black text-navy lg:hidden`}>
            {step.no}
          </span>
          <span className="overline mt-4 block lg:mt-0">Step {step.no}</span>
          <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl lg:text-4xl">
            {step.title}
          </h2>
          <p className="mt-5 text-base leading-relaxed text-steel lg:text-lg">{step.body}</p>
        </div>
      </Reveal>
    </div>
  );
};

const Timeline = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start center", "end center"] });
  const scaleY = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section className="container-px mx-auto max-w-[1400px] py-20 lg:py-28">
      <div ref={ref} className="relative flex flex-col gap-20 lg:gap-32">
        {/* Center spine (desktop) */}
        <div className="absolute left-1/2 top-0 hidden h-full w-px -translate-x-1/2 bg-border lg:block" aria-hidden>
          <motion.div style={{ scaleY }} className="h-full w-full origin-top bg-brand-green" />
        </div>
        {STEPS.map((step, i) => (
          <Step key={step.no} step={step} index={i} />
        ))}
      </div>
    </section>
  );
};

export default function HowWeWork() {
  return (
    <>
      <Seo
        title="How We Work"
        description="Our process: customized cleaning plan, written schedule and responsibilities, a consistent site team, weekly follow-up, rapid problem resolution and daily management oversight."
        path="/how-we-work"
      />
      <PageHero
        eyebrow="How We Work"
        title={["A precise,", "accountable process."]}
      />

      <Timeline />

      <CtaBand
        title="Schedule a Facility Walkthrough"
        body=""
        primary={{ label: "Request a Customized Cleaning Plan", to: "/request-a-quote" }}
        secondary={{ label: "Schedule a Facility Walkthrough", to: "/request-a-quote" }}
      />
    </>
  );
}
